import { Feature, Map, MapBrowserEvent } from "ol";
import { EventsKey } from "ol/events";
import Geometry from "ol/geom/Geometry";
import BaseLayer from "ol/layer/Base";
import VectorLayer from "ol/layer/Vector";
import { unByKey } from "ol/Observable";
import { useEffect } from "react";
import { useAccess } from "~/utils/hooks/useAccess";
import { findLayerByFeatureId } from "../functions/findLayerByFeatureId";
import { findSourceByTitle } from "../functions/findSourceByTitle";
import { LayerTitle } from "../types/layers";
import { MapItem } from "../types/mapItems";

type UseSeaSurveyEventHandlerReturn = void;

export function setSeaSurveyHoverStyle(
  feat: Feature<Geometry>,
  layer: BaseLayer | null,
  map: Map,
): void {
  const sf = (layer as VectorLayer)?.getStyleFunction();
  if (!sf) return;
  const resolution = map.getView().getResolution();
  if (!resolution) return;
  const layerStyle = sf(feat, resolution);
  if (Array.isArray(layerStyle)) return;
  const newStyle = layerStyle.clone();
  const icon = layerStyle.getImage()?.clone();
  const scale = icon?.getScale();
  const stroke = layerStyle.getStroke()?.clone();
  const fill = layerStyle.getFill()?.clone();
  const strokeWidth = stroke?.getWidth();
  if (icon && typeof scale === "number") {
    // Not used for now
    icon.setScale(scale * 1.2);
    if (false) newStyle.setImage(icon);
  } else if (stroke && strokeWidth && fill) {
    stroke.setWidth(strokeWidth * 2);
    fill.setColor("rgba(159,39,176,0.8)");
    newStyle.setStroke(stroke);
    newStyle.setFill(fill);
    newStyle.setZIndex(10);
  }
  feat.setStyle(newStyle);
}

export function clearSeaSurveysHoverStyle(map: Map): void {
  const source = findSourceByTitle(map, LayerTitle.Measurements);
  source.forEachFeature((f) => f.setStyle());
}

export function useSeaSurveyEventHandler(
  map: Map | undefined,
): UseSeaSurveyEventHandlerReturn {
  const { SeaSurveyRead } = useAccess();
  useEffect(() => {
    let key: EventsKey | undefined;
    const listener = (e: MapBrowserEvent<UIEvent>) => {
      if (!map) return;
      clearSeaSurveysHoverStyle(map);
      // Adjust feature style if hovered
      const features = map.getFeaturesAtPixel(e.pixel, {
        layerFilter: (l) => l instanceof VectorLayer,
      });
      if (!features?.length) return;
      const surveyFeatures = features.filter(
        (f) => (f.getProperties() as MapItem)?.type === "measurement",
      );
      if (!surveyFeatures?.length) return;
      // Highlight the first one
      const feat = surveyFeatures[0] as Feature<Geometry>;
      const id = feat.getId();
      if (!id) return;
      const layer = findLayerByFeatureId(map, id.toString());
      if (!layer) return;
      setSeaSurveyHoverStyle(feat, layer, map);
    };
    if (SeaSurveyRead) key = map?.on("pointermove", listener);
    return () => {
      if (key) unByKey(key);
    };
  }, [map, SeaSurveyRead]);
}
